/**
 * "This model with Skills" block on the Pelican Test model page.
 *
 * The Lab hub (a separate Worker on the same origin) runs Lab Skills on the
 * pelican theme across models and exposes them at
 * `GET /lab/api/pelican-compare?model=<provider/model>`. This block lists the
 * Skills the current model has runs for, each linking to the Skill page's
 * `#pelican` section. It renders nothing while loading, on error or when the
 * list is empty, so it stays invisible until the hub ships the endpoint.
 */
import { useEffect, useState } from 'react'
import { labApi } from './api'
import { SectionHead } from './components'
import { formatCost } from './format'
import type { Dict } from './i18n'
import { useT } from './i18n'
import type { Lang } from './lang'
import { useLang } from './prefs'

export interface SkillRunLink {
  slug: string
  title: { en: string; 'zh-CN': string }
  skillVersion: string
  status: string
  costUsd: number | null
  poster: string | null
  href: string
  runAt: string
}

const HREF = /^\/lab\/[a-z0-9-]+#pelican$/
const POSTER = /^\/lab\/downloads\/[A-Za-z0-9._/-]+$/
const str = (v: unknown): v is string => typeof v === 'string'

/** Keeps only well-formed same-origin items from the hub response; anything else is dropped. */
export function parseSkillRuns(data: unknown): SkillRunLink[] {
  const skills = (data as { skills?: unknown } | null)?.skills
  if (!Array.isArray(skills)) return []
  const out: SkillRunLink[] = []
  for (const raw of skills) {
    if (!raw || typeof raw !== 'object') continue
    const r = raw as Record<string, unknown>
    const title = r.title as Record<string, unknown> | null | undefined
    if (!str(r.slug) || !str(r.href) || !HREF.test(r.href)) continue
    if (r.poster != null && (!str(r.poster) || !POSTER.test(r.poster) || r.poster.includes('..'))) continue
    const en = title && str(title.en) ? title.en : r.slug
    out.push({
      slug: r.slug,
      title: { en, 'zh-CN': title && str(title['zh-CN']) ? title['zh-CN'] : en },
      skillVersion: str(r.skillVersion) ? r.skillVersion : '',
      status: str(r.status) ? r.status : '',
      costUsd: typeof r.costUsd === 'number' && Number.isFinite(r.costUsd) ? r.costUsd : null,
      poster: str(r.poster) ? r.poster : null,
      href: r.href,
      runAt: str(r.runAt) ? r.runAt : '',
    })
  }
  return out
}

/** Card meta line: cost when finished; skipped / unavailable runs are not failures, so they get neutral copy. */
function statusMeta(s: SkillRunLink, t: Dict): string {
  if (s.status === 'ok') return t.skills.cost(formatCost(s.costUsd))
  if (s.status === 'skipped') return t.skills.skipped
  if (s.status === 'unavailable') return t.skills.unavailable
  return t.skills.failed
}

export function SkillRunsList({ items, lang, t }: { items: SkillRunLink[]; lang: Lang; t: Dict }) {
  if (items.length === 0) return null
  return (
    <section className="section" id="skills" aria-labelledby="skills-title">
      <div className="measure">
        <SectionHead id="skills-title" eyebrow={t.skills.eyebrow} title={t.skills.title} lede={t.skills.lede} />
        <ul className="skill-runs">
          {items.map((s) => {
            const name = s.title[lang] ?? s.title.en
            const meta = statusMeta(s, t)
            return (
              <li key={s.slug}>
                {/* Plain anchor: the Skill page lives in the hub app, not this router. */}
                <a href={s.href} className="browse-tile skill-run" data-status={s.status}>
                  {s.poster
                    ? <img src={s.poster} alt={t.skills.posterAlt(name)} loading="lazy" decoding="async" width={640} height={360} />
                    : null}
                  <span className="browse-name">{name}</span>
                  <span className="browse-meta">{s.skillVersion ? `v${s.skillVersion} · ` : ''}{meta}</span>
                  <span className="browse-meta skill-run-open">{t.skills.open}</span>
                </a>
              </li>
            )
          })}
        </ul>
      </div>
    </section>
  )
}

export function SkillRunsSection({ model }: { model: string }) {
  const t = useT()
  const { lang } = useLang()
  const [items, setItems] = useState<SkillRunLink[]>([])
  useEffect(() => {
    let cancelled = false
    setItems([])
    labApi<unknown>(`/lab/api/pelican-compare?model=${encodeURIComponent(model)}`)
      .then((data) => { if (!cancelled) setItems(parseSkillRuns(data)) })
      .catch(() => { /* hidden until the hub serves this endpoint */ })
    return () => { cancelled = true }
  }, [model])
  return <SkillRunsList items={items} lang={lang} t={t} />
}
